import * as React from 'react'
import { Controller } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import Layout from '../../components/controls/Layout'
import * as Input from '@avcan/utils/dom/input'
import * as Intl from 'hooks/intl'
import { DateParam, TimeParam } from '@avcan/utils/params'
import { createExtraPropsFromOptions } from '../../components/controls/Input'
import { isValid } from '@avcan/utils/date'
import { range } from '@avcan/utils/array'
import styles from './styles.module.css'

export default function DateTime({ props, options }) {
    const { label, description, name, type } = props

    return (
        <Layout name={name} label={label} description={description}>
            {Input.isTypeSupported('datetime-local') ? (
                <Controller name={name} as={<DateTimeLocal options={options} type={type} />} />
            ) : (
                <Controller name={name} as={<UnsupportedDateTime options={options} />} />
            )}
        </Layout>
    )
}

// Util components
function DateTimeLocal({ options, type, value, ...props }) {
    function onChange(event) {
        const date = new Date(event.target.value)

        props.onChange(date.toISOString())
    }

    return (
        <input
            id={props.name}
            type="datetime-local"
            value={value ? format(new Date(value)) : ''}
            onChange={onChange}
            {...createExtraPropsFromOptions(options, type)}
        />
    )
}
function UnsupportedDateTime({ name, value, onChange, onBlur, options }) {
    const description = 'Layouts - Form -  Date & Time'
    const [date, setDate] = React.useState(DateParam.format(DateParam.parse(value)))
    const [time, setTime] = React.useState(TimeParam.format(TimeParam.parse(value)))
    const handleDateChange = React.useCallback(event => {
        setDate(event.target.value)
    }, [])
    const handleTimeChange = React.useCallback(event => {
        setTime(event.target.value)
    }, [])

    React.useEffect(() => {
        if (typeof value === 'undefined') {
            setDate('')
            setTime('')
        }
    }, [value])

    React.useEffect(() => {
        if (date && time) {
            const dateTime = new Date(date + 'T00:00:00')

            if (isValid(dateTime)) {
                dateTime.setHours(...time.split(':').map(Number))

                onChange(dateTime.toISOString())
            }
        } else {
            onChange('')
        }
    }, [date, time, onChange])

    return (
        <div className={styles.Wrapper}>
            {Input.isTypeSupported('date') ? (
                <div>
                    <label htmlFor={name + '-date'}>
                        <FormattedMessage description={description} defaultMessage="Date" />
                    </label>
                    <input
                        id={name + '-date'}
                        type="date"
                        value={date}
                        onChange={handleDateChange}
                        onBlur={onBlur}
                        {...createExtraPropsFromOptions(options, 'date')}
                    />
                </div>
            ) : (
                <UnsupportedDate value={date} onChange={setDate} />
            )}
            {Input.isTypeSupported('time') ? (
                <div>
                    <label htmlFor={name + '-time'}>
                        <FormattedMessage description={description} defaultMessage="Time" />
                    </label>
                    <input
                        id={name + '-time'}
                        type="time"
                        value={time}
                        onChange={handleTimeChange}
                        onBlur={onBlur}
                    />
                </div>
            ) : (
                <UnsupportedTime value={time} onChange={setTime} />
            )}
        </div>
    )
}
export function UnsupportedDate({ value = '', onChange }) {
    const description = 'UnsupportedDate input'
    const months = Intl.useMonths()
    const years = useYears(2)
    const [data, onInpuChange] = useBasicForm(() => {
        const [year = '', month = '', date = ''] = value.split('-')

        return { year, month, date }
    })

    React.useEffect(() => {
        const parts = [data.year, data.month, data.date]

        if (parts.every(Boolean)) {
            onChange(parts.join('-'))
        }
    }, [data, onChange])

    return (
        <fieldset className={styles.UnsupportedDate}>
            <legend>
                <FormattedMessage description={description} defaultMessage="Date" />
            </legend>
            <label>
                <span>
                    <FormattedMessage description={description} defaultMessage="Year" />
                </span>
                <select name="year" value={data.year} onChange={onInpuChange}>
                    <EmptyOption />
                    {years.map(year => (
                        <option value={year}>{year}</option>
                    ))}
                </select>
            </label>
            <label>
                <span>
                    <FormattedMessage description={description} defaultMessage="Month" />
                </span>
                <select name="month" value={data.month} onChange={onInpuChange}>
                    <EmptyOption />
                    {months.map((month, index) => {
                        const value = String(index + 1).padStart(2, 0)

                        return (
                            <option key={index} value={value}>
                                {month}
                            </option>
                        )
                    })}
                </select>
            </label>
            <label>
                <span>
                    <FormattedMessage description={description} defaultMessage="Date" />
                </span>
                <select name="date" value={data.date} onChange={onInpuChange}>
                    <EmptyOption />
                    <NumberOptions numbers={range(1, getNumberOfDays(data.year, data.month))} />
                </select>
            </label>
        </fieldset>
    )
}
function UnsupportedTime({ value = '', onChange }) {
    const description = 'UnsupportedTime input'
    const [data, onInpuChange] = useBasicForm(() => {
        const [hours = '', minutes = ''] = value.split(':')

        return { hours, minutes }
    })

    React.useEffect(() => {
        const parts = [data.hours, data.minutes]

        if (parts.every(Boolean)) {
            onChange(parts.join(':'))
        }
    }, [data, onChange])

    return (
        <fieldset className={styles.UnsupportedTime}>
            <legend>
                <FormattedMessage description={description} defaultMessage="Time" />
            </legend>
            <label>
                <span>
                    <FormattedMessage description={description} defaultMessage="Hours" />
                </span>
                <select name="hours" value={data.hours} onChange={onInpuChange}>
                    <EmptyOption />
                    <NumberOptions numbers={range(24)} />
                </select>
            </label>
            <label>
                <span>
                    <FormattedMessage description={description} defaultMessage="Minutes" />
                </span>
                <select name="minutes" value={data.minutes} onChange={onInpuChange}>
                    <EmptyOption />
                    <NumberOptions numbers={range(0, 55, 5)} />
                </select>
            </label>
        </fieldset>
    )
}

function NumberOptions({ numbers }) {
    return numbers.map(number => {
        const value = String(number).padStart(2, 0)

        return <option value={value}>{value}</option>
    })
}

function EmptyOption() {
    const intl = useIntl()

    return (
        <option>
            {intl.formatMessage({
                id: 'select',
                defaultMessage: 'Select',
            })}
        </option>
    )
}

// Utils
function useBasicForm(initialValues) {
    const [data, setData] = React.useState(initialValues)
    const onChange = React.useCallback(event => {
        const { name, value } = event.target

        setData(data => ({ ...data, [name]: value }))
    }, [])

    return [data, onChange]
}
function format(date = new Date()) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, 0)}-${String(
        date.getDate()
    ).padStart(2, 0)}T${String(date.getHours()).padStart(2, 0)}:${String(
        date.getMinutes()
    ).padStart(2, 0)}`
}
function getNumberOfDays(year, month) {
    switch (Number(month)) {
        case 2: // February
            const isLeap = new Date(Number(year), 1, 29).getMonth() === 1

            return isLeap ? 29 : 28
        case 4: // April
        case 6: // June
        case 9: // September
        case 11: // November
            return 30
        default:
            return 31
    }
}
function useYears(amount = 10) {
    return React.useMemo(() => {
        const YEAR = new Date().getFullYear()

        return Array.from(Array(amount).keys(), index => YEAR - index)
    }, [amount])
}
